import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { allArticles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);
  const cat = categories.find((c) => c.slug === article?.category);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pt-28 pb-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您查找的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catArticles = cat
    ? cat.articles
        .map((a) => allArticles.find((aa) => aa.slug === a))
        .filter(Boolean)
        .filter((a) => a.slug !== article.slug)
        .slice(0, 3)
    : [];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <Breadcrumb />

        <article className="w-full pb-16 md:pb-20">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6 pt-4">
            <div className="mb-8">
              {cat && (
                <Link
                  to={`/category/${cat.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium mb-4 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  {cat.name}
                </Link>
              )}
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  发布于 {article.publishDate}
                </span>
                {cat && (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-price-tag-3-line"></i>
                    {cat.name}
                  </span>
                )}
              </div>
            </div>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Detailed%20scientific%20illustration%20of%20wild%20mushrooms%20with%20anatomical%20labels%20and%20cross%20sections%2C%20educational%20botanical%20study%20in%20warm%20cream%20and%20brown%20tones%2C%20vintage%20naturalist%20style%2C%20clean%20composition%2C%20soft%20lighting&width=1200&height=675&seq=article-hero&orientation=landscape"
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-sm prose-p:md:text-base prose-p:mb-5
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="text-sm text-foreground-500">
                  免责声明：本文仅供科普参考，不能替代专业医疗建议。野外采摘蘑菇请务必在有经验人士指导下进行。
                </div>
                <Link
                  to={cat ? `/category/${cat.slug}` : '/'}
                  className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{cat ? cat.name : '首页'}
                </Link>
              </div>
            </div>
          </div>
        </article>

        {catArticles.length > 0 && (
          <section className="w-full py-12 md:py-16 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {catArticles.map((related) => (
                  <Link
                    key={related?.slug}
                    to={`/article/${related?.slug}`}
                    className="group bg-background-50 rounded-xl p-5 md:p-6 hover:bg-background-200/60 transition-all duration-300"
                  >
                    <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {related?.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {related?.excerpt}
                    </p>
                    <span className="text-xs text-primary-600 font-medium">
                      阅读全文 →
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}